import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { isLexUser } from '@/utils/lexDomain';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Users, Search, FileText, LogOut, Calculator,
  ChevronRight, UserPlus, Mail, Inbox as InboxIcon, AlertCircle,
  CheckSquare, ArrowRight, TrendingUp, Building2, Plus, CalendarClock, X, Scale,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AdvisorFeedbackButton from '@/components/AdvisorFeedbackButton';
import HelpOverlay, { useHelpOverlay, HelpButton } from '@/components/HelpOverlay';
import { ChangelogButton } from '@/components/changelog/ChangelogPanel';
// Feb 2026 — DACH-Smart-Inputs (Phase 8)
import { PhoneInput } from '@/components/inputs/SmartInputs';
// Juni 2026 — SB-Onboarding Batch 4: Mandant komplett einrichten
import ClientOnboardWizard from '@/components/advisor/ClientOnboardWizard';
import { useAdvisorAuth } from '@/contexts/AdvisorAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const H = { fontFamily: 'Manrope, sans-serif' };

function getAdvisorHeaders() {
  // Cookie-Auth (T1): advisor_access_token kommt als httpOnly-Cookie → kein Header nötig.
  return {};
}

// ────────── Top Navigation (matches FL app pattern) ──────────
function AdvisorTopNav({ advisor, badge, onLogout, onInbox, onSettings, onBilling }) {
  const items = [
    { label: 'Übersicht', active: true },
    { label: 'Mandanten', active: false, onClick: () => document.getElementById('all-clients-section')?.scrollIntoView({ behavior: 'smooth' }) },
    { label: 'Posteingang', active: false, badge, onClick: onInbox },
    { label: 'Abrechnung', active: false, onClick: onBilling },
    { label: 'Einstellungen', active: false, onClick: onSettings },
  ];
  return (
    <header className="border-b border-slate-200/70 bg-white/80 backdrop-blur-xl sticky top-0 z-30">
      <div className="flex h-16 items-center gap-2 px-4 sm:px-6 lg:px-8">
        <div className="mr-3 flex items-center gap-2">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-emerald-600 shadow-sm shadow-emerald-600/20">
            <Calculator className="h-5 w-5 text-white" />
          </div>
          <span className="font-semibold tracking-tight text-slate-900" style={H} data-testid="brand-logo">ivoliq</span>
          <span className="ml-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] uppercase tracking-widest text-emerald-700">SB</span>
        </div>
        <nav className="flex flex-1 items-center gap-1 overflow-x-auto">
          {items.map((item) => (
            <button
              key={item.label}
              onClick={item.onClick}
              className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                item.active ? 'bg-emerald-100 font-medium text-emerald-700' : 'text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`nav-${item.label.toLowerCase()}`}
            >
              <span className="hidden md:inline">{item.label}</span>
              <span className="md:hidden">{item.label.slice(0, 2)}</span>
              {item.badge > 0 && (
                <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-emerald-600 px-1.5 text-[10px] font-bold text-white" data-testid={`nav-badge-${item.label.toLowerCase()}`}>
                  {item.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
        {advisor && (
          <div className="hidden text-right sm:block">
            <div className="text-sm font-medium text-slate-900" data-testid="advisor-name">{advisor.name}</div>
            <div className="text-xs text-slate-500">{advisor.company_name || advisor.email}</div>
          </div>
        )}
        <ChangelogButton audience="sb" className="ml-1" />
        <button onClick={onLogout} className="ml-2 text-slate-400 hover:text-slate-700" title="Ausloggen" data-testid="logout-btn">
          <LogOut className="h-4 w-4" />
        </button>
      </div>
    </header>
  );
}

// ────────── KPI Card (Premium-Redesign Juni 2026: Tiefe + Hover-Lift) ──────────
function KPICard({ title, value, subtitle, Icon, iconColor = 'bg-emerald-50 text-emerald-600', borderColor = 'border-t-emerald-500', testId, onClick, active }) {
  const clickable = typeof onClick === 'function';
  return (
    <Card
      onClick={onClick}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      className={`group relative overflow-hidden rounded-lg border border-slate-200 shadow-none transition-all duration-200 hover:-translate-y-1 hover:shadow-lg hover:shadow-slate-200/60 ${clickable ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2' : ''} ${active ? 'ring-2 ring-emerald-500 ring-offset-1' : ''}`}
      data-testid={testId}
    >
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400">{title}</p>
            <p className="font-mono text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{value}</p>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          <div className={`rounded-2xl p-3 ${iconColor} transition-transform duration-200 group-hover:scale-110`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
        {clickable && (
          <ChevronRight className="absolute bottom-3 right-3 h-4 w-4 text-slate-300 opacity-0 transition-opacity duration-200 group-hover:opacity-100" />
        )}
      </CardContent>
    </Card>
  );
}

// ────────── Action Item Row (in "Heute brauchen dich") ──────────
function ClientActionRow({ client, onOpen }) {
  const initials = (client.client_name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const lastActivity = client.last_activity ? new Date(client.last_activity) : null;
  const ago = lastActivity ? formatAgo(lastActivity) : '—';
  return (
    <div
      className="flex items-center gap-3 rounded-lg p-3 transition-colors hover:bg-slate-50 cursor-pointer"
      onClick={() => onOpen(client.client_id)}
      data-testid={`triage-client-${client.client_id}`}
    >
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-50 font-mono text-xs font-semibold text-emerald-700">
        {initials || '?'}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-900">{client.client_name || 'Unbekannt'}</p>
        <p className="text-xs text-slate-500">zuletzt aktiv {ago}</p>
      </div>
      {client.urgent_for_me > 0 && (
        <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
          {client.urgent_for_me} dringend
        </span>
      )}
      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
        {client.waiting_for_me} {client.waiting_for_me === 1 ? 'Vorgang' : 'Vorgänge'}
      </span>
      <ChevronRight className="h-4 w-4 text-slate-400" />
    </div>
  );
}

function formatAgo(d) {
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  if (diff < 604800) return `vor ${Math.floor(diff / 86400)} Tagen`;
  return d.toLocaleDateString('de-DE');
}

// ────────── UStVA-Ampel-Badge (SB-Cockpit, Juni 2026) ──────────
function UstvaBadge({ client }) {
  const status = client.ustva_status;
  const map = {
    ready: { cls: 'border-emerald-200 bg-emerald-50 text-emerald-700', dot: 'bg-emerald-500' },
    open: { cls: 'border-amber-200 bg-amber-50 text-amber-700', dot: 'bg-amber-500' },
    overdue: { cls: 'border-rose-200 bg-rose-50 text-rose-700', dot: 'bg-rose-500' },
    none: { cls: 'border-slate-200 bg-slate-50 text-slate-500', dot: 'bg-slate-300' },
  };
  const m = map[status] || map.none;
  const fmtDate = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };
  const days = client.days_until_deadline;
  return (
    <div className="flex flex-col gap-1" data-testid={`client-ustva-${client.client_id}`}>
      <span className={`inline-flex w-fit items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-medium ${m.cls}`}>
        <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
        {client.ustva_status_label || 'Unbekannt'}
      </span>
      {client.next_deadline ? (
        <span className={`text-[11px] ${status === 'overdue' ? 'font-medium text-rose-600' : 'text-slate-500'}`}>
          {client.next_deadline_period} · {fmtDate(client.next_deadline)}
          {typeof days === 'number' && (status !== 'ready') && (
            <span className="text-slate-400"> ({days < 0 ? `${Math.abs(days)} T. überfällig` : `in ${days} T.`})</span>
          )}
        </span>
      ) : (
        client.ustva_reason && <span className="text-[11px] text-slate-400">{client.ustva_reason}</span>
      )}
    </div>
  );
}

// ────────── Empty States ──────────
function EmptyTriage({ onInvite }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-200 bg-white px-6 py-12 text-center">
      <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50">
        <CheckSquare className="h-6 w-6 text-emerald-600" />
      </div>
      <h3 className="text-base font-semibold text-slate-900" style={H}>Alles erledigt</h3>
      <p className="mt-1 max-w-md mx-auto text-sm text-slate-500">
        Aktuell gibt es keine offenen Vorgänge, die deine Aufmerksamkeit brauchen. Sobald ein Mandant antwortet, einen Beleg hochlädt oder eine Frage stellt, taucht es hier auf.
      </p>
      {onInvite && (
        <Button onClick={onInvite} className="mt-5 bg-emerald-600 text-white hover:bg-emerald-700">
          <UserPlus className="mr-2 h-4 w-4" /> Mandant einladen
        </Button>
      )}
    </div>
  );
}

// ────────── All Clients Table (legacy view, now secondary) ──────────
function AllClientsSection({ clients, search, setSearch, onOpen, onInvite, onOnboard, onResendActivation, invitations, onCancelSent, defaultCollapsed = false, openItemsByClient = {}, ustvaFilter = false, onClearUstvaFilter, ifbFilter = false, onClearIfbFilter, onFilterIfb }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Re-evaluate when defaultCollapsed prop changes (e.g. after data fetch)
  useEffect(() => { setCollapsed(defaultCollapsed); }, [defaultCollapsed]);
  const filtered = clients.filter((c) => {
    if (ustvaFilter && c.ustva_status !== 'overdue') return false;
    if (ifbFilter && c.ifb_status !== 'unused') return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (c.name || '').toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.company_name || '').toLowerCase().includes(q)
    );
  });
  return (
    <Card id="all-clients-section" className="rounded-2xl border border-slate-100 shadow-sm">
      <CardContent className="p-5 sm:p-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            type="button"
            onClick={() => setCollapsed(c => !c)}
            className="flex-1 text-left rounded-md -ml-1 px-1 py-0.5 hover:bg-slate-50 transition-colors"
            data-testid="all-clients-toggle"
            aria-expanded={!collapsed}
          >
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900" style={H}>Alle Mandanten</h2>
              <span className="text-xs text-slate-400 font-mono">{collapsed ? '▶' : '▼'}</span>
            </div>
            <p className="text-xs text-slate-500">
              <span className="font-mono">{clients.length}</span> aktiv
              {invitations?.sent?.filter(i => i.status === 'pending')?.length > 0 && (
                <> · <span className="font-mono">{invitations.sent.filter(i => i.status === 'pending').length}</span> Einladung{invitations.sent.filter(i => i.status === 'pending').length === 1 ? '' : 'en'} ausstehend</>
              )}
            </p>
          </button>
          {!collapsed && (
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Mandanten suchen…"
                  className="pl-9 w-64"
                  data-testid="search-clients"
                />
              </div>
              <Button
                onClick={onOnboard}
                className="bg-slate-900 text-white hover:bg-slate-800"
                title="Account komplett einrichten — Kanzlei übernimmt die Pro-Lizenz."
                data-testid="onboard-client-btn"
              >
                <Plus className="mr-2 h-4 w-4" /> Mandant anlegen
              </Button>
              <Button
                onClick={onInvite}
                className="bg-emerald-600 text-white hover:bg-emerald-700"
                title="Mandant mit eigenem ivoliq-Account einladen — Mandant zahlt selbst."
                data-testid="invite-client-btn"
              >
                <UserPlus className="mr-2 h-4 w-4" /> Mandant einladen
              </Button>
            </div>
          )}
        </div>
        {!collapsed && ustvaFilter && (
          <div className="mb-3">
            <span
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800"
              data-testid="filter-chip-ustva"
            >
              Filter: UStVA überfällig
              <button
                onClick={onClearUstvaFilter}
                className="rounded-full p-0.5 hover:bg-emerald-100"
                title="Filter zurücksetzen"
                data-testid="filter-chip-ustva-clear"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
        {!collapsed && ifbFilter && (
          <div className="mb-3">
            <span
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-800"
              data-testid="filter-chip-ifb"
            >
              Filter: Investitionsfreibetrag offen (AT)
              <button
                onClick={onClearIfbFilter}
                className="rounded-full p-0.5 hover:bg-emerald-100"
                title="Filter zurücksetzen"
                data-testid="filter-chip-ifb-clear"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          </div>
        )}
        {!collapsed && (clients.length === 0 ? (
          <div className="rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center">
            <Users className="mx-auto mb-3 h-8 w-8 text-slate-300" />
            <p className="text-sm font-medium text-slate-900">Noch keine Mandanten</p>
            <p className="mt-1 text-xs text-slate-500">Lade deinen ersten Mandanten ein, um loszulegen.</p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Mandant</th>
                  <th className="px-4 py-3 font-medium">Plan</th>
                  <th className="px-4 py-3 font-medium">UStVA-Status</th>
                  <th className="px-4 py-3 font-medium">Belege</th>
                  <th className="px-4 py-3 font-medium">Rechnungen</th>
                  <th className="px-4 py-3 font-medium">Offene Vorgänge</th>
                  <th className="px-4 py-3 text-right font-medium">Aktion</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((c) => (
                  <tr key={c.client_id} className="hover:bg-slate-50 cursor-pointer" onClick={() => onOpen(c.client_id)} data-testid={`client-row-${c.client_id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 font-mono text-[11px] font-semibold text-slate-700">
                          {(c.name || '?').slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{c.name || 'Unbekannt'}</p>
                          <p className="text-xs text-slate-500">{c.company_name || c.email}</p>
                          {c.ifb_status === 'unused' && (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); onFilterIfb?.(); }}
                              className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-100 transition-colors"
                              title="Nur Mandanten mit offenem Investitionsfreibetrag anzeigen"
                              data-testid={`client-ifb-badge-${c.client_id}`}
                            >
                              💡 IFB offen: {Math.round(c.ifb_unused_amount || 0).toLocaleString('de-DE')} €
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {c.account_status === 'pending_activation' ? (
                        <div className="flex flex-col gap-1">
                          <span className="inline-flex w-fit items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700" data-testid={`pending-activation-badge-${c.client_id}`}>
                            ⏳ Aktivierung offen
                          </span>
                          <button
                            onClick={(e) => { e.stopPropagation(); onResendActivation?.(c.client_id); }}
                            className="w-fit text-[11px] text-emerald-600 hover:underline"
                            data-testid={`resend-activation-btn-${c.client_id}`}
                          >
                            Link erneut senden
                          </button>
                        </div>
                      ) : (
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider text-slate-600">
                          {c.license_mode === 'sb_paid' ? 'PRO · Kanzlei' : c.plan}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3"><UstvaBadge client={c} /></td>
                    <td className="px-4 py-3 font-mono text-slate-700">{c.expense_count}</td>
                    <td className="px-4 py-3 font-mono text-slate-700">{c.invoice_count}</td>
                    <td className="px-4 py-3">
                      {(() => {
                        const oi = openItemsByClient[c.client_id] || {};
                        const waiting = oi.waiting_for_me || 0;
                        const urgent = oi.urgent_for_me || 0;
                        if (waiting === 0) {
                          return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />erledigt</span>;
                        }
                        return (
                          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${urgent > 0 ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-slate-200 bg-slate-50 text-slate-600'}`} data-testid={`client-open-items-${c.client_id}`}>
                            {waiting} offen{urgent > 0 ? ` · ${urgent} dringend` : ''}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ChevronRight className="ml-auto h-4 w-4 text-slate-400" />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500">
                      Keine Mandanten gefunden für „{search}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        ))}

        {!collapsed && invitations?.sent?.filter(i => i.status === 'pending')?.length > 0 && (
          <div className="mt-5 rounded-lg border border-amber-200 bg-amber-50 p-4" data-testid="advisor-sent-invitations">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-amber-700">
              Ausstehende Einladungen ({invitations.sent.filter(i => i.status === 'pending').length})
            </p>
            <div className="space-y-1">
              {invitations.sent.filter(i => i.status === 'pending').map((inv) => (
                <div key={inv.id} className="flex items-center gap-2 text-sm text-amber-900" data-testid={`sent-invite-${inv.id}`}>
                  <Mail className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                  <span className="truncate">{inv.to_email}</span>
                  <span className="ml-auto text-xs text-amber-700 shrink-0">{new Date(inv.created_at).toLocaleDateString('de-DE')}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onCancelSent(inv.token)}
                    className="h-7 px-2 text-xs text-rose-600 hover:bg-rose-50 hover:text-rose-700 shrink-0"
                    data-testid={`cancel-sent-invite-${inv.id}`}
                  >
                    Stornieren
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ────────── Invite Modal (C5-Cleanup, Feb 2026: Magic-Link aus UI entfernt — nur E-Mail) ──────────
function InviteModal({ open, onClose, onSent, onSwitchToOnboard }) {
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  if (!open) return null;

  const submit = async () => {
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/advisor/invitations/create`, { to_email: email.trim() }, { headers: getAdvisorHeaders() });
      toast.success('Einladung gesendet');
      setEmail('');
      onSent();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Versenden');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>Mandant einladen</h3>
          <p className="mt-1 text-sm text-slate-500">
            Für Mandanten, die <strong>bereits einen eigenen ivoliq-Account</strong> haben oder sich
            selbst registrieren — der Mandant zahlt seine Lizenz selbst. Wir versenden eine
            Einladungs-E-Mail mit einem persönlichen Aktivierungs-Link.
          </p>
          {/* Feb 2026 — Pro-Gating-Hinweis */}
          <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="invite-pro-hint">
            <strong>Hinweis:</strong> Mandanten benötigen den Pro-Plan oder eine Lifetime-Lizenz, um die SB-Anbindung zu nutzen. Free- und Basic-Konten können nicht verbunden werden.
          </div>
          {onSwitchToOnboard && (
            <div className="mt-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600" data-testid="invite-switch-hint">
              Du möchtest den Account stattdessen <strong>komplett einrichten</strong> und die
              Pro-Lizenz übernehmen?{' '}
              <button
                onClick={onSwitchToOnboard}
                className="font-medium text-emerald-600 underline hover:text-emerald-700"
                data-testid="invite-switch-to-onboard-btn"
              >
                Mandant anlegen
              </button>
            </div>
          )}

          <div className="mt-4">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
              E-Mail des Mandanten
            </label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="lena@beispiel.de"
              data-testid="invite-email-input"
              type="email"
              autoFocus
            />
          </div>

          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button onClick={submit} disabled={submitting || !email.trim()} className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="invite-submit-btn">
              {submitting ? '…' : 'Senden'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ────────── Pending Incoming Invitations (Feb 2026, Issue #3 fix) ──────────
function PendingIncomingInvitations({ invitations, onAccept, onReject }) {
  const pending = (invitations?.received || []).filter(
    (i) => i.status === 'pending' && i.type === 'freelancer_to_advisor'
  );
  if (pending.length === 0) return null;
  return (
    <Card className="mb-6 border-l-4 border-l-amber-500" data-testid="incoming-invitations-card">
      <CardContent className="p-5 sm:p-6">
        <div className="mb-3 flex items-center gap-2">
          <div className="rounded-lg bg-amber-50 p-1.5">
            <Mail className="h-4 w-4 text-amber-600" />
          </div>
          <h2 className="text-base font-semibold text-slate-900" style={H}>
            Eingehende Mandanten-Einladungen ({pending.length})
          </h2>
        </div>
        <div className="space-y-2">
          {pending.map((inv) => (
            <div
              key={inv.id}
              className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3"
              data-testid={`incoming-invite-${inv.id}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {inv.from_user_name || 'Mandant'}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  möchte dich als Steuerberater einladen · seit{' '}
                  {new Date(inv.created_at).toLocaleDateString('de-DE')}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onReject(inv.token)}
                  className="text-rose-600 border-rose-200 hover:bg-rose-50"
                  data-testid={`reject-incoming-invite-${inv.id}`}
                >
                  Ablehnen
                </Button>
                <Button
                  size="sm"
                  onClick={() => onAccept(inv.token)}
                  className="bg-emerald-600 text-white hover:bg-emerald-700"
                  data-testid={`accept-incoming-invite-${inv.id}`}
                >
                  Annehmen
                  <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ────────── Settings Modal (Feb 2026, Issue #4 fix) ──────────
function AdvisorSettingsModal({ open, onClose, advisor, onSaved }) {
  const [name, setName] = useState(advisor?.name || '');
  const [companyName, setCompanyName] = useState(advisor?.company_name || '');
  const [phone, setPhone] = useState(advisor?.phone || '');
  const [street, setStreet] = useState(advisor?.street || '');
  const [streetNumber, setStreetNumber] = useState(advisor?.street_number || '');
  const [postalCode, setPostalCode] = useState(advisor?.postal_code || '');
  const [city, setCity] = useState(advisor?.city || '');
  const [vatId, setVatId] = useState(advisor?.vat_id || '');
  const [saving, setSaving] = useState(false);
  React.useEffect(() => {
    if (advisor) {
      setName(advisor.name || '');
      setCompanyName(advisor.company_name || '');
      setPhone(advisor.phone || '');
      setStreet(advisor.street || '');
      setStreetNumber(advisor.street_number || '');
      setPostalCode(advisor.postal_code || '');
      setCity(advisor.city || '');
      setVatId(advisor.vat_id || '');
    }
  }, [advisor]);
  if (!open) return null;
  const submit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/advisor/profile`,
        {
          name, company_name: companyName, phone,
          street, street_number: streetNumber, postal_code: postalCode, city, vat_id: vatId,
        },
        { headers: getAdvisorHeaders() }
      );
      toast.success('Einstellungen gespeichert');
      onSaved && onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4"
      onClick={onClose}
    >
      <Card className="w-full max-w-md max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold tracking-tight text-slate-900" style={H}>
            Einstellungen
          </h3>
          <p className="mt-1 text-sm text-slate-500">
            Profil-Informationen — werden deinem Mandanten in der App angezeigt.
          </p>
          <div className="mt-4 space-y-3">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Name
              </label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="settings-name"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Kanzlei
              </label>
              <Input
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Kanzlei Müller & Partner"
                data-testid="settings-company"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">
                Telefon (optional)
              </label>
              <PhoneInput
                country="DE"
                value={phone}
                onChange={(v) => setPhone(v)}
                label={null}
                data-testid="settings-phone"
              />
            </div>
            <div className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500">
              <strong className="text-slate-700">E-Mail:</strong> {advisor?.email}
              <span className="ml-2 text-slate-400">(nicht änderbar)</span>
            </div>

            {/* Rechnungsadresse (Juni 2026): erscheint auf den ivoliq-Rechnungen an die Kanzlei */}
            <div className="pt-2 border-t border-slate-100">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Rechnungsadresse der Kanzlei</p>
              <p className="text-[11px] text-slate-400 mt-0.5">Erscheint auf deinen ivoliq-Rechnungen.</p>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Straße</label>
                <Input value={street} onChange={(e) => setStreet(e.target.value)} data-testid="settings-street" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Nr.</label>
                <Input value={streetNumber} onChange={(e) => setStreetNumber(e.target.value)} data-testid="settings-street-number" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">PLZ</label>
                <Input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} data-testid="settings-postal-code" />
              </div>
              <div className="col-span-2">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Ort</label>
                <Input value={city} onChange={(e) => setCity(e.target.value)} data-testid="settings-city" />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">UID / USt-IdNr.</label>
              <Input value={vatId} onChange={(e) => setVatId(e.target.value)} placeholder="ATU12345678 / DE123456789" data-testid="settings-vat-id" />
            </div>
          </div>
          <div className="mt-5 flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <Button
              onClick={submit}
              disabled={saving}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              data-testid="settings-save-btn"
            >
              {saving ? '…' : 'Speichern'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ────────── Main Component ──────────
export default function AdvisorDashboard() {
  const navigate = useNavigate();
  // Zentraler Auth-Zustand (gemeinsamer Provider) — kein eigener /me-Check mehr.
  const { advisor, loading: authLoading, logout, refresh } = useAdvisorAuth();
  const [clients, setClients] = useState([]);
  const [inbox, setInbox] = useState({ clients: [], totals: {} });
  const [invitations, setInvitations] = useState({ sent: [], received: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [ustvaFilter, setUstvaFilter] = useState(false); // Clickable-KPI-Filter (Juni 2026)
  const [ifbFilter, setIfbFilter] = useState(false); // Investitionsfreibetrag-Filter (Juni 2026)
  const [showInviteModal, setShowInviteModal] = useState(false);
  // Juni 2026 — SB-Onboarding Batch 4
  const [showOnboardWizard, setShowOnboardWizard] = useState(false);
  // Feb 2026 — Issue #4: Posteingang/Einstellungen funktional machen
  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const handleAcceptIncomingInvitation = useCallback(async (token) => {
    try {
      await axios.post(
        `${API}/advisor/invitations/${token}/accept-advisor`,
        {},
        { headers: getAdvisorHeaders() }
      );
      toast.success('Einladung angenommen — Mandant verbunden!');
      // refresh
      const [c, inv] = await Promise.all([
        axios.get(`${API}/advisor/clients`, { headers: getAdvisorHeaders() }),
        axios.get(`${API}/advisor/invitations`, { headers: getAdvisorHeaders() }).catch(() => ({ data: { sent: [], received: [] } })),
      ]);
      setClients(c.data.clients || []);
      setInvitations(inv.data || { sent: [], received: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Annehmen');
    }
  }, []);

  const handleRejectIncomingInvitation = useCallback(async (token) => {
    if (!window.confirm('Einladung wirklich ablehnen?')) return;
    try {
      await axios.post(
        `${API}/advisor/invitations/${token}/reject-advisor`,
        {},
        { headers: getAdvisorHeaders() }
      );
      toast.success('Einladung abgelehnt');
      const inv = await axios.get(`${API}/advisor/invitations`, { headers: getAdvisorHeaders() }).catch(() => ({ data: { sent: [], received: [] } }));
      setInvitations(inv.data || { sent: [], received: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Ablehnen');
    }
  }, []);

  const handleCancelSentInvitation = useCallback(async (token) => {
    if (!window.confirm('Gesendete Einladung wirklich stornieren?')) return;
    try {
      await axios.post(
        `${API}/advisor/invitations/${token}/cancel-advisor`,
        {},
        { headers: getAdvisorHeaders() }
      );
      toast.success('Einladung storniert');
      const inv = await axios.get(`${API}/advisor/invitations`, { headers: getAdvisorHeaders() }).catch(() => ({ data: { sent: [], received: [] } }));
      setInvitations(inv.data || { sent: [], received: [] });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Stornieren');
    }
  }, []);

  const handleScrollToInbox = useCallback(() => {
    const el = document.querySelector('[data-testid="triage-action-card"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const fetchAll = useCallback(async () => {
    if (!advisor) return;
    try {
      const [c, i, inv] = await Promise.all([
        axios.get(`${API}/advisor/clients`, { headers: getAdvisorHeaders() }),
        axios.get(`${API}/collab/inbox/advisor`, { headers: getAdvisorHeaders() }),
        axios.get(`${API}/advisor/invitations`, { headers: getAdvisorHeaders() }).catch(() => ({ data: { sent: [], received: [] } })),
      ]);
      setClients(c.data.clients || []);
      setInbox(i.data || { clients: [], totals: {} });
      setInvitations(inv.data || { sent: [], received: [] });
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        // Daten-Endpoint gab 401/403. Nicht blind ausloggen (sonst „Dashboard
        // blitzt auf → Login-Maske"). Erst die Session via /me gegenprüfen und
        // NUR bei echtem Auth-Verlust abmelden.
        try {
          await axios.get(`${API}/advisor/auth/me`, { headers: getAdvisorHeaders() });
          toast.error('Mandantendaten konnten nicht geladen werden.');
        } catch {
          logout();
        }
      }
    } finally {
      setLoading(false);
    }
  }, [advisor, logout]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Poll every 60s for fresh badge counts
  useEffect(() => {
    if (!advisor) return undefined;
    const t = setInterval(() => {
      axios.get(`${API}/collab/inbox/advisor`, { headers: getAdvisorHeaders() })
        .then((res) => setInbox(res.data || { clients: [], totals: {} }))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(t);
  }, [advisor]);

  const handleLogout = () => {
    axios.post(`${API}/advisor/auth/logout`, {}).catch(() => {});
    logout();
    navigate('/steuerberater');
  };

  const openClient = (clientId) => navigate(`/advisor/client/${clientId}`);

  // Juni 2026 — SB-Onboarding: Aktivierungslink erneut senden
  const handleResendActivation = useCallback(async (clientId) => {
    try {
      await axios.post(`${API}/advisor/clients/${clientId}/resend-activation`, {}, { headers: getAdvisorHeaders() });
      toast.success('Aktivierungslink erneut gesendet.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Link konnte nicht gesendet werden.');
    }
  }, []);

  // Help-Overlay (Iter 72) — `?` öffnet Tastatur-Hilfe für SB-Onboarding
  const help = useHelpOverlay();

  // Hotkeys: i = Mandant einladen, m = Mandanten-Suche fokussieren, /? = Suche
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'i') {
        e.preventDefault();
        setShowInviteModal(true);
      } else if (k === 'm' || e.key === '/') {
        e.preventDefault();
        const el = document.querySelector('[data-testid="search-clients"]');
        if (el) {
          // Mandantenliste evtl. eingeklappt → ausklappen
          const toggle = document.querySelector('[data-testid="all-clients-toggle"]');
          if (toggle && toggle.getAttribute('aria-expanded') === 'false') toggle.click();
          setTimeout(() => el.focus(), 80);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Auth-Gate (deklarativ): erst Spinner während Check, dann Login-Redirect
  // oder Inhalt. Keine gegenseitige Navigation mehr → kein Flackern.
  if (authLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-emerald-600" />
      </div>
    );
  }
  if (!advisor) {
    return <Navigate to="/steuerberater" replace />;
  }
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-slate-200 border-t-emerald-600" />
      </div>
    );
  }

  const totals = inbox.totals || {};
  const actionClients = (inbox.clients || []).filter((c) => c.waiting_for_me > 0);
  const ustvaOverdueCount = (clients || []).filter((c) => c.ustva_status === 'overdue').length;
  // Greeting: pick a sensible display name (skip titles like "Dr.")
  const displayName = (() => {
    const parts = (advisor.name || '').split(/\s+/).filter(Boolean);
    const skip = new Set(['Dr.', 'Dr', 'Prof.', 'Prof', 'Mag.', 'Mag', 'Dipl.', 'Dipl']);
    const first = parts.find((p) => !skip.has(p));
    return first || advisor.name || 'Sabine';
  })();
  const greeting = (() => {
    const h = new Date().getHours();
    if (h < 11) return 'Guten Morgen';
    if (h < 18) return 'Guten Tag';
    return 'Guten Abend';
  })();

  return (
    <div className="min-h-screen bg-slate-50" style={{ fontFamily: 'DM Sans, sans-serif' }}>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=JetBrains+Mono:wght@400;600&family=Manrope:wght@400;500;600;700&display=swap" />

      <AdvisorTopNav
        advisor={advisor}
        badge={totals.waiting_for_me || 0}
        onLogout={handleLogout}
        onInbox={handleScrollToInbox}
        onSettings={() => setShowSettingsModal(true)}
        onBilling={() => navigate('/advisor/abrechnung')}
      />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {/* Eingehende Einladungen (Feb 2026, Issue #3 fix) */}
        <PendingIncomingInvitations
          invitations={invitations}
          onAccept={handleAcceptIncomingInvitation}
          onReject={handleRejectIncomingInvitation}
        />

        {/* Greeting */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900" style={H} data-testid="greeting">
              {greeting}, {displayName}
            </h1>
            {totals.waiting_for_me > 0 ? (
              <p className="mt-1 text-sm text-slate-500" data-testid="greeting-subtitle">
                <span className="font-mono font-medium text-slate-900">{totals.waiting_for_me}</span> Vorgänge warten auf dich — verteilt auf{' '}
                <span className="font-mono font-medium text-slate-900">{totals.clients_with_action}</span>{' '}
                {totals.clients_with_action === 1 ? 'Mandanten' : 'Mandanten'}.
              </p>
            ) : (
              <p className="mt-1 text-sm text-slate-500" data-testid="greeting-subtitle">
                Alles erledigt — keine offenen Vorgänge.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {(clients || []).some((c) => isLexUser(c)) && (
              <Button
                onClick={() => navigate('/kanzlei')}
                variant="outline"
                className="border-[#C5A059] text-[#0A1128] hover:bg-[#FBF7EF]"
                title="Cockpit für lex.ivoliq-Mandanten (AT-Rechtsanwälte): Ampel, Grenzen, Fristen, Anomalien."
                data-testid="header-lex-cockpit-btn"
              >
                <Scale className="mr-2 h-4 w-4" /> lex-Cockpit
              </Button>
            )}
            <Button
              onClick={() => setShowOnboardWizard(true)}
              className="bg-slate-900 text-white hover:bg-slate-800"
              title="Du richtest den Account komplett ein — deine Kanzlei übernimmt die Pro-Lizenz."
              data-testid="header-onboard-btn"
            >
              <Plus className="mr-2 h-4 w-4" /> Mandant anlegen
            </Button>
            <Button
              onClick={() => setShowInviteModal(true)}
              className="bg-emerald-600 text-white hover:bg-emerald-700"
              title="Für Mandanten mit eigenem ivoliq-Account — der Mandant zahlt selbst."
              data-testid="header-invite-btn"
            >
              <UserPlus className="mr-2 h-4 w-4" /> Mandant einladen
            </Button>
            <HelpButton onClick={help.toggle} testId="advisor-dashboard-help-btn" size="md" />
          </div>
        </div>

        {/* KPI Row */}
        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <KPICard
            title="Warten auf mich"
            value={totals.waiting_for_me || 0}
            subtitle={`${totals.clients_with_action || 0} ${totals.clients_with_action === 1 ? 'Mandant' : 'Mandanten'}`}
            Icon={InboxIcon}
            borderColor="border-t-emerald-500"
            iconColor="bg-emerald-50 text-emerald-600"
            testId="kpi-waiting-for-me"
          />
          <KPICard
            title="UStVA überfällig"
            value={ustvaOverdueCount}
            subtitle="Mandanten mit überschrittener Frist"
            Icon={CalendarClock}
            borderColor="border-t-rose-500"
            iconColor="bg-rose-50 text-rose-600"
            testId="kpi-ustva-overdue"
            active={ustvaFilter}
            onClick={ustvaOverdueCount > 0 ? () => {
              setUstvaFilter(true);
              const toggle = document.querySelector('[data-testid="all-clients-toggle"]');
              if (toggle && toggle.getAttribute('aria-expanded') === 'false') toggle.click();
              setTimeout(() => {
                document.querySelector('#all-clients-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 80);
            } : undefined}
          />
          <KPICard
            title="Quartale zur Freigabe"
            value={totals.quarters_pending || 0}
            subtitle="periodische Reviews offen"
            Icon={CheckSquare}
            borderColor="border-t-blue-500"
            iconColor="bg-blue-50 text-blue-600"
            testId="kpi-quarters-pending"
          />
          <KPICard
            title="Frisch hochgeladen"
            value={totals.recent_uploads || 0}
            subtitle="Belege letzte 24h"
            Icon={FileText}
            borderColor="border-t-slate-500"
            iconColor="bg-slate-100 text-slate-600"
            testId="kpi-recent-uploads"
          />
          <KPICard
            title="Überfällig"
            value={totals.overdue_for_me || 0}
            subtitle="Vorgänge > 7 Tage"
            Icon={AlertCircle}
            borderColor="border-t-amber-500"
            iconColor="bg-amber-50 text-amber-600"
            testId="kpi-overdue"
          />
        </div>

        {/* Action Card — "Heute brauchen dich" */}
        <Card className="mb-8 rounded-2xl border border-slate-100 border-l-4 border-l-emerald-500 shadow-sm" data-testid="triage-action-card">
          <CardContent className="p-5 sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded-lg bg-emerald-50 p-1.5">
                  <TrendingUp className="h-4 w-4 text-emerald-600" />
                </div>
                <h2 className="text-base font-semibold text-slate-900" style={H}>Heute brauchen dich</h2>
              </div>
              {actionClients.length > 0 && (
                <span className="text-xs text-slate-500">sortiert nach Dringlichkeit</span>
              )}
            </div>
            {actionClients.length === 0 ? (
              <EmptyTriage onInvite={clients.length === 0 ? () => setShowInviteModal(true) : null} />
            ) : (
              <div className="space-y-1">
                {actionClients.map((c) => (
                  <ClientActionRow key={c.client_id} client={c} onOpen={openClient} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* All clients (secondary) — collapsible (C7-Cleanup, Feb 2026):
            wenn ≥3 Mandanten Aktion brauchen, blenden wir die volle Liste standardmäßig
            aus, damit der Triage-Fokus oben bleibt. */}
        <AllClientsSection
          clients={clients}
          search={search}
          setSearch={setSearch}
          onOpen={openClient}
          onInvite={() => setShowInviteModal(true)}
          onOnboard={() => setShowOnboardWizard(true)}
          onResendActivation={handleResendActivation}
          invitations={invitations}
          onCancelSent={handleCancelSentInvitation}
          defaultCollapsed={actionClients.length >= 3}
          openItemsByClient={Object.fromEntries((inbox.clients || []).map((c) => [c.client_id, c]))}
          ustvaFilter={ustvaFilter}
          onClearUstvaFilter={() => setUstvaFilter(false)}
          ifbFilter={ifbFilter}
          onClearIfbFilter={() => setIfbFilter(false)}
          onFilterIfb={() => { setUstvaFilter(false); setIfbFilter(true); }}
        />
      </main>

      <InviteModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSent={() => fetchAll()}
        onSwitchToOnboard={() => { setShowInviteModal(false); setShowOnboardWizard(true); }}
      />
      <ClientOnboardWizard
        open={showOnboardWizard}
        onClose={() => setShowOnboardWizard(false)}
        onSuccess={() => fetchAll()}
        authHeaders={getAdvisorHeaders()}
      />
      <AdvisorSettingsModal
        open={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        advisor={advisor}
        onSaved={() => {
          // Profil nach Speichern neu laden (zentraler Auth-Provider).
          refresh();
        }}
      />
      <AdvisorFeedbackButton />

      <HelpOverlay
        isOpen={help.isOpen}
        onClose={help.close}
        title="SB-Dashboard — Tastatur-Shortcuts"
        sections={[
          {
            title: 'Aktionen',
            shortcuts: [
              { keys: ['?'], label: 'Diese Hilfe öffnen / schließen' },
              { keys: ['I'], label: 'Mandant einladen' },
              { keys: ['M'], label: 'Mandanten-Suche fokussieren' },
              { keys: ['/'], label: 'Mandanten-Suche fokussieren' },
            ],
          },
          {
            title: 'Navigation',
            shortcuts: [
              { keys: ['Klick', 'Mandant'], label: 'Mandanten-Detail öffnen' },
              { keys: ['Klick', '„Heute brauchen dich"'], label: 'Direkt zum offenen Vorgang' },
            ],
          },
          {
            title: 'Mandantenliste',
            shortcuts: [
              { keys: ['Klick', 'auf Titel'], label: 'Volle Mandantenliste ein- / ausblenden' },
            ],
          },
        ]}
      />
    </div>
  );
}
